import { useLayoutEffect, useRef } from 'react'
import type { CSSProperties } from 'react'
import { Cloche } from '../../animations/Cloche'
import { scrollIntoFrame } from '../../animations/scrollIntoFrame'
import { DIETARY_LABEL } from '../../dietary'
import type { InvitedPerson } from '../../types'

interface GuestDietarySummaryProps {
  person: InvitedPerson
  /**
   * True when this has just been submitted, rather than being how the page
   * arrived. Only then is there anything to reveal.
   */
  reveal: boolean
  onChange: () => void
}

/**
 * How long after the dome starts lifting the answer appears, and the gap before
 * the button under it.
 *
 * Both scale with the lift in animations.css - they are what makes it read as
 * uncovering the answer rather than as two animations in a queue, so slowing the
 * dome without slowing these would have the card arrive under a dome that has
 * barely moved.
 */
const FIRST_SERVE_MS = 750
const SERVE_GAP_MS = 325

/**
 * What replaces one guest's form once they have answered.
 *
 * Submitting lifts a cloche off the answer. A guest who merely lands on a card
 * they filled in weeks ago gets it plainly, the same way somebody who has
 * already replied is not made to sit through the dog again.
 */
export function GuestDietarySummary({ person, reveal, onChange }: GuestDietarySummaryProps) {
  const cardRef = useRef<HTMLDivElement>(null)

  /*
   * Bring the card into frame when it replaces the form.
   *
   * The guest's scroll offset stays where it was through the swap, which leaves
   * them looking at whatever moved up into where they were reading - and with
   * the cloche lifting off screen above them. A layout effect, so this runs
   * after the swap has been laid out but before the browser paints it: the
   * correction and the reflow land in the same frame rather than as a visible
   * jump.
   */
  useLayoutEffect(() => {
    if (!reveal || !cardRef.current) return
    scrollIntoFrame(cardRef.current, 'center')
  }, [reveal])

  const serve = (index: number): CSSProperties | undefined =>
    reveal
      ? ({ '--serve-delay': `${FIRST_SERVE_MS + index * SERVE_GAP_MS}ms` } as CSSProperties)
      : undefined
  const served = reveal ? 'animate-serve' : ''

  return (
    <div
      ref={cardRef}
      // No entrance fade when revealing: the card would be fading in at the same
      // time as the dome is fading out, and the two multiply - the dome peaked at
      // 14% opacity, which read as a smudge rather than a cloche. Worse, this
      // card inherits `--reveal-delay` from the staggered wrapper in InviteView,
      // so the card sat at zero for the dome's first 280ms. The dome *is* the
      // entrance here; the fade is only wanted when the page is arriving.
      className={`border-ink/10 bg-parchment/70 relative rounded-lg border p-5 shadow-sm sm:p-6 ${
        reveal ? '' : 'animate-reveal'
      }`}
      style={reveal ? undefined : ({ '--reveal-delay': '0ms' } as CSSProperties)}
      role="status"
      aria-live="polite"
    >
      {/* Centred over the answer it is covering, and allowed to rise clear of the
          card - it fades as it goes, so overlapping what is above reads as being
          lifted away rather than as a clipping mistake. */}
      {reveal && (
        <div className="pointer-events-none absolute inset-x-0 top-14 z-10 mx-auto w-56 max-w-[70%]">
          <Cloche />
        </div>
      )}

      <div className="flex items-baseline justify-between gap-3">
        <h3 className="font-display min-w-0 text-xl">{person.name}</h3>
        <p className="text-sage shrink-0 text-xs tracking-widest uppercase">Noted</p>
      </div>

      <dl className="mt-4 text-sm">
        <div className={served} style={serve(0)}>
          <dt className="text-ink-soft text-xs tracking-widest uppercase">Dietary requirements</dt>
          {/* Stated even when there are none. This is the whole of the answer,
              and a card that showed nothing would look like we had not asked. */}
          <dd className="mt-0.5">
            {person.dietary.length > 0
              ? person.dietary.map((requirement) => DIETARY_LABEL[requirement]).join(', ')
              : 'None'}
          </dd>
        </div>
      </dl>

      <button
        type="button"
        onClick={onChange}
        className={`border-ink/20 hover:border-claret hover:text-claret mt-5 rounded-md border px-5 py-2.5 text-sm tracking-widest uppercase transition-colors ${served}`}
        style={serve(1)}
      >
        Change {person.name.split(' ')[0]}&rsquo;s requirements
      </button>
    </div>
  )
}
